import { useEffect, useRef, useState } from 'react';

const TICK_MS = 15_000;
const USAGE_PREFIX = 'orbit_screen_usage_';

const pad = (value) => String(value).padStart(2, '0');

export function getScreenTimeDateKey(date = new Date()) {
  return `${USAGE_PREFIX}${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function getMsUntilMidnight(date = new Date()) {
  const midnight = new Date(date);
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime() - date.getTime();
}

function readUsageSeconds(date = new Date()) {
  try {
    const value = Number(localStorage.getItem(getScreenTimeDateKey(date)));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function writeUsageSeconds(seconds, date = new Date()) {
  try {
    localStorage.setItem(getScreenTimeDateKey(date), String(seconds));
  } catch {
    // Tracking remains in memory when localStorage is unavailable.
  }
}

export function useScreenTime(isAuthenticated) {
  const [usedSeconds, setUsedSeconds] = useState(() => readUsageSeconds());
  const [resetInMs, setResetInMs] = useState(() => getMsUntilMidnight());
  const activeSince = useRef(null);
  const activeDateKey = useRef(getScreenTimeDateKey());
  const inMemorySeconds = useRef(usedSeconds);

  useEffect(() => {
    const isVisible = () => document.visibilityState === 'visible';

    const syncDate = (now) => {
      const dateKey = getScreenTimeDateKey(now);
      if (dateKey === activeDateKey.current) return false;
      activeDateKey.current = dateKey;
      const seconds = readUsageSeconds(now);
      inMemorySeconds.current = seconds;
      setUsedSeconds(seconds);
      return true;
    };

    const accrue = (now, includeElapsed = false) => {
      const dateChanged = syncDate(now);
      setResetInMs(getMsUntilMidnight(now));

      if (!isAuthenticated || (!includeElapsed && !isVisible())) {
        activeSince.current = null;
        return;
      }
      if (dateChanged || activeSince.current === null) {
        activeSince.current = now.getTime();
        return;
      }

      const elapsedSeconds = Math.max(0, (now.getTime() - activeSince.current) / 1000);
      const seconds = Math.max(readUsageSeconds(now), inMemorySeconds.current) + elapsedSeconds;
      inMemorySeconds.current = seconds;
      writeUsageSeconds(seconds, now);
      setUsedSeconds(seconds);
      activeSince.current = now.getTime();
    };

    const handleVisibilityChange = () => {
      const now = new Date();
      if (isVisible()) {
        syncDate(now);
        setResetInMs(getMsUntilMidnight(now));
        activeSince.current = isAuthenticated ? now.getTime() : null;
      } else {
        accrue(now, true);
        activeSince.current = null;
      }
    };

    activeSince.current = isAuthenticated && isVisible() ? Date.now() : null;
    const interval = setInterval(() => accrue(new Date()), TICK_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  return {
    usedMinutes: Math.floor(usedSeconds / 60),
    resetInMs,
  };
}
